import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getInitials } from '@/lib/helpers';
import { cn } from '@/lib/utils';
import {
  Mails,
  NotepadText,
  Plus,
  Settings,
  Users,
  User,
  Clock,
  Shield,
  Building2,
  LogOut,
  LayoutDashboard,
  Handshake,
  Ship,
  Wrench,
  DollarSign,
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
} from '@marine/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@marine/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import { Button } from '@marine/ui/button';
import { ScrollArea } from '@marine/ui/scroll-area';
import { useLogout, useUser } from '@/hooks/useAuth';
import { motion } from 'framer-motion';

interface SidebarPrimaryProps {
  onLogout?: () => void;
  isLogoutPending?: boolean;
  portalType?: 'platform' | 'tenant';
  enabledModules?: string[];
}

// Detect active module index from URL path
function getActiveModuleIndex(pathname: string, items: NavItem[]): number {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.href !== '#' && item.href !== '/dashboard') {
      if (pathname.startsWith(item.href)) return i;
    }
  }
  return 0; // dashboard
}

export interface NavItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  className: string;
  moduleKey?: string;
}

// Platform admin navigation items
export const platformNavItems: NavItem[] = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    href: '/dashboard',
    className: 'border-white bg-violet-500 hover:bg-violet-600 text-white hover:text-white',
  },
  {
    icon: Building2,
    label: 'Tenants',
    href: '/platform/tenants',
    className: 'border-white bg-teal-500 hover:bg-teal-600 text-white hover:text-white',
  },
  {
    icon: Users,
    label: 'Users',
    href: '/users',
    className: 'border-white bg-blue-500 hover:bg-blue-600 text-white hover:text-white',
  },
  {
    icon: Settings,
    label: 'Settings',
    href: '/settings',
    className: 'border-white bg-gray-500 hover:bg-gray-600 text-white hover:text-white',
  },
];

// All tenant module navigation items (will be filtered by enabled modules)
export const allTenantModules: NavItem[] = [
  {
    icon: LayoutDashboard,
    label: 'Dashboard',
    href: '/dashboard',
    className: 'border-white bg-violet-500 hover:bg-violet-600 text-white hover:text-white',
    moduleKey: 'core', // Core is always enabled
  },
  {
    icon: Ship,
    label: 'Inventory',
    href: '/inventory',
    className: 'border-white bg-lime-500 hover:bg-lime-600 text-white hover:text-white',
    moduleKey: 'inventory',
  },
  {
    icon: Handshake,
    label: 'CRM',
    href: '/crm',
    className: 'border-white bg-teal-500 hover:bg-teal-600 text-white hover:text-white',
    moduleKey: 'crm',
  },
  {
    icon: Wrench,
    label: 'Service',
    href: '/service',
    className: 'border-white bg-blue-500 hover:bg-blue-600 text-white hover:text-white',
    moduleKey: 'service',
  },
  {
    icon: DollarSign,
    label: 'Finance',
    href: '/finance',
    className: 'border-white bg-yellow-500 hover:bg-yellow-600 text-white hover:text-white',
    moduleKey: 'finance',
  },
];

// Add module button (shown on tenant portal)
const addModuleItem: NavItem = {
  icon: Plus,
  label: 'Add Module',
  href: '#',
  className: 'border border-border bg-background text-foreground hover:bg-background hover:text-foreground',
};

export function SidebarPrimary({ 
  onLogout, 
  isLogoutPending,
  portalType = 'platform',
  enabledModules = ['core'],
}: SidebarPrimaryProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const defaultLogoutMutation = useLogout();
  const user = useUser();

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      defaultLogoutMutation.mutate();
    }
  };

  const isPending = isLogoutPending ?? defaultLogoutMutation.isPending;
  const displayName =
    user?.full_name ||
    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
    user?.email ||
    'User';
  const displayRole = user?.roles?.[0]?.name || 'User';
  const avatarSrc = user?.avatar_url || undefined;
  const initials = getInitials(displayName, 2) || 'U';
  
  // Compute nav items based on portal type
  const navItems: NavItem[] = portalType === 'platform' 
    ? platformNavItems 
    : [
        // Filter tenant modules by enabled modules
        ...allTenantModules.filter(item => 
          item.moduleKey && enabledModules.includes(item.moduleKey)
        ),
        // Add the "Add Module" button for tenants
        addModuleItem,
      ];

  const activeModuleIndex = getActiveModuleIndex(pathname, navItems);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <div className="flex flex-col items-center justify-between shrink-0 py-2.5 gap-5 w-[70px] lg:w-(--sidebar-collapsed-width) border-e border-input bg-muted">
      {/* Module Navigation */}
      <div className="shrink-0 grow w-full relative">
        <ScrollArea className="shrink-0 h-[calc(100dvh-22rem)]">
          <div className="flex flex-col grow items-center gap-[10px] shrink-0"> 
            {/* Moving indicator bar */}
            <motion.div
              className="absolute left-1.75 w-0.5 h-3 bg-green-600 rounded-full z-10"
              animate={{
                y: (hoveredIndex !== null ? hoveredIndex : activeModuleIndex) * 44 + 11.5,
              }}
              whileHover={{
                scaleY: 1.5,
                scaleX: 1.2,
                backgroundColor: "#059669",
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                duration: 0.2
              }}
            />
            {navItems.map((item, index) => (
              <Tooltip key={item.label}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    mode="icon"
                    className={cn(
                      'transition-all duration-300 rounded-lg shadow-sm border-2 hover:shadow-[0_4px_12px_0_rgba(37,47,74,0.35)] w-[34px] h-[34px]',
                      item.className
                    )}
                    onClick={() => item.href !== '#' && navigate(item.href)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <item.icon className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Footer */}
      <div className="flex flex-col items-center gap-2.5 shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" mode="icon" className="text-muted-foreground hover:text-foreground">
              <Mails className="opacity-100"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Messages</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" mode="icon" className="text-muted-foreground hover:text-foreground">
              <NotepadText className="opacity-100"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Notes</TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" mode="icon" className="text-muted-foreground hover:text-foreground">
              <Settings className="opacity-100"/>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Settings</TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger className="cursor-pointer mb-2.5">
            <Avatar className="size-7">
              {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
              <AvatarFallback>{initials}</AvatarFallback>
              <AvatarIndicator className="-end-2 -top-2">
                <AvatarStatus variant="online" className="size-2.5" />
              </AvatarIndicator>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 mb-4" side="right" align="start" sideOffset={11}>
            {/* User Information Section */}
            <div className="flex items-center gap-3 px-3 py-2">
              <Avatar>
                {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
                <AvatarFallback>{initials}</AvatarFallback>
                <AvatarIndicator className="-end-1.5 -top-1.5">
                  <AvatarStatus variant="online" className="size-2.5" />
                </AvatarIndicator>
              </Avatar>
              <div className="flex flex-col items-start">
                <span className="text-sm font-semibold text-foreground">{displayName}</span>
                <span className="text-xs text-muted-foreground">{displayRole}</span>
              </div>
            </div>

            <DropdownMenuItem className="cursor-pointer py-1 rounded-md border border-border hover:bg-muted">
              <Clock/>
              <span>Set availability</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Settings */}
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User/>
              <span>Profile Settings</span>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => navigate('/profile/preferences')}>
              <Settings/>
              <span>Preferences</span>
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => navigate('/profile/security')}>
              <Shield/>
              <span>Security</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Action Items */}
            <DropdownMenuItem
              onClick={handleLogout}
              disabled={isPending}
            >
              <LogOut/>
              <span>{isPending ? 'Signing out...' : 'Sign out'}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
